import React, { useEffect, useMemo, useState } from 'react';
import { Device, DeviceCalibration as Pt, deviceLabel, getDevice, listDevices } from '../../api/device';
import { ApiError } from '../../api/request';
import SearchSelect from '../../components/SearchSelect';
import { useDictText } from '../../components/DictSelect';

// 双向插值: % → W 与 W → % (后者用于"想要 N W 的真实功率, 软件该设到几%").

function interp(points: Pt[], pct: number): number {
    if (points.length === 0) return NaN;
    const s = [...points].sort((a, b) => a.pct - b.pct);
    if (pct <= s[0].pct) return s[0].realW;
    if (pct >= s[s.length - 1].pct) return s[s.length - 1].realW;
    for (let i = 0; i < s.length - 1; i++) {
        if (pct >= s[i].pct && pct <= s[i + 1].pct) {
            const t = (pct - s[i].pct) / (s[i + 1].pct - s[i].pct);
            return s[i].realW + t * (s[i + 1].realW - s[i].realW);
        }
    }
    return NaN;
}

function interpInv(points: Pt[], w: number): number {
    if (points.length === 0) return NaN;
    const s = [...points].sort((a, b) => a.realW - b.realW);
    if (w <= s[0].realW) return s[0].pct;
    if (w >= s[s.length - 1].realW) return s[s.length - 1].pct;
    for (let i = 0; i < s.length - 1; i++) {
        if (w >= s[i].realW && w <= s[i + 1].realW) {
            const t = (w - s[i].realW) / (s[i + 1].realW - s[i].realW);
            return s[i].pct + t * (s[i + 1].pct - s[i].pct);
        }
    }
    return NaN;
}

interface Props {
    /** 受控 deviceKey: 若传入, 由父组件管理选择; 否则组件自管选择并展示设备下拉. */
    deviceKey?: string;
    /** 是否在头部显示自带的设备下拉 (默认 true). */
    showDeviceSelect?: boolean;
    /** 是否显示标题行 (默认 true). */
    showTitle?: boolean;
    /** 窄容器 (如 1/3 宽卡片) 传 true: 两个换算方向上下排, 避免挤成一团 (默认 false 左右两列). */
    stacked?: boolean;
}

function PowerCalcWidget({
    deviceKey: externalKey,
    showDeviceSelect = true,
    showTitle = true,
    stacked = false,
}: Props) {
    // 设备展示名的光源段走字典 label (light_source), 不裸显 code
    const lightText = useDictText('light_source');
    const [devices, setDevices] = useState<Device[]>([]);
    const [internalKey, setInternalKey] = useState<string>('');
    const deviceKey = externalKey ?? internalKey;
    const [calibration, setCalibration] = useState<Pt[]>([]);
    const [stdW, setStdW] = useState<number>(0);
    const [minStablePower, setMinStablePower] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [pctInput, setPctInput] = useState<string>('50');
    const [wInput, setWInput] = useState<string>('5');

    useEffect(() => {
        if (externalKey || !showDeviceSelect) return;
        listDevices({ page: 1, pageSize: 100 })
            .then((res) => {
                setDevices(res.list || []);
                if (res.list && res.list.length > 0 && !internalKey) {
                    setInternalKey(res.list[0].deviceKey);
                }
            })
            .catch((e) => {
                const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
                setError(msg);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalKey, showDeviceSelect]);

    useEffect(() => {
        if (!deviceKey) {
            setCalibration([]);
            return;
        }
        setLoading(true);
        getDevice(deviceKey)
            .then((d) => {
                setCalibration(d.calibration || []);
                setStdW(d.capability?.stdW || 0);
                // 最小稳定功率阈值按 % 比对 (belowStable 的比较对象是 pct); 旧代码读不存在的
                // minStablePower 键导致告警从未触发, 2026-08-14 迁 capability 时一并修正.
                setMinStablePower(d.capability?.minStablePct || 0);
                setError('');
            })
            .catch((e) => {
                const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
                setError(msg);
                setCalibration([]);
            })
            .finally(() => setLoading(false));
    }, [deviceKey]);

    const pct = parseFloat(pctInput);
    const wOut = useMemo(
        () => (!isNaN(pct) && calibration.length > 0 ? interp(calibration, pct) : NaN),
        [pct, calibration],
    );
    const wReq = parseFloat(wInput);
    const pctOut = useMemo(
        () => (!isNaN(wReq) && calibration.length > 0 ? interpInv(calibration, wReq) : NaN),
        [wReq, calibration],
    );

    const belowStable = minStablePower > 0 && !isNaN(pct) && pct < minStablePower;

    return (
        <div className="space-y-3">
            {showTitle && (
                <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold">设备</span>
                    {showDeviceSelect && !externalKey && (
                        <SearchSelect
                            className="w-72"
                            value={deviceKey}
                            onChange={setInternalKey}
                            placeholder="搜 设备 / 架构"
                            options={devices.map((d) => ({
                                value: d.deviceKey,
                                label: `${d.archCode} · ${deviceLabel(d, lightText)}`,
                            }))}
                        />
                    )}
                    {stdW > 0 && (
                        <span className="tag bg-slate-100 text-slate-600">标准 {stdW}W</span>
                    )}
                </div>
            )}

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    {error}
                </div>
            )}

            <div className={stacked ? 'space-y-5' : 'grid grid-cols-2 gap-6'}>
                {/* % → W */}
                <div className="space-y-2">
                    <label className="field-label">软件功率 (%) → 真实功率 (W)</label>
                    <div className="flex items-center gap-3">
                        <input
                            type="range"
                            min={0}
                            max={100}
                            step={0.5}
                            value={pctInput}
                            onChange={(e) => setPctInput(e.target.value)}
                            className="flex-1"
                        />
                        <input
                            type="number"
                            min={0}
                            max={100}
                            step={0.5}
                            value={pctInput}
                            onChange={(e) => setPctInput(e.target.value)}
                            className="inp w-20"
                        />
                        <span className="text-slate-400">%</span>
                    </div>
                    <div className="text-3xl font-semibold text-brand-600">
                        {loading || isNaN(wOut) ? '—' : wOut.toFixed(3)}{' '}
                        <span className="text-base text-slate-400">W</span>
                    </div>
                    {belowStable && (
                        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                            ⚠ 低于最小稳定功率 ({minStablePower}%), 实际激光可能不稳
                        </div>
                    )}
                </div>

                {/* W → % */}
                <div className={stacked ? 'space-y-2 pt-4 border-t border-slate-100' : 'space-y-2'}>
                    <label className="field-label">想要真实功率 (W) → 软件设几% (反查)</label>
                    <div className="flex items-center gap-3">
                        <input
                            type="number"
                            min={0}
                            step={0.1}
                            value={wInput}
                            onChange={(e) => setWInput(e.target.value)}
                            className="inp w-32"
                        />
                        <span className="text-slate-400">W</span>
                    </div>
                    <div className="text-3xl font-semibold text-brand-600">
                        {loading || isNaN(pctOut) ? '—' : pctOut.toFixed(1)}{' '}
                        <span className="text-base text-slate-400">%</span>
                    </div>
                    <div className="text-xs text-slate-400">
                        基于 {calibration.length} 个校准点线性反查
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PowerCalcWidget;
